import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { showBasicAlert } from "../common/useAlert";
import CommonSelect, { Option } from "../common/commonSelect";
import { useSelector } from "react-redux";
import ImageSelectDropMenu from "../../feature-module/administration/imageSelectDropMenu";
import { getEmployeeImageUrl } from "../../services/assetCommonService";

interface Ticket {
  id: number;
  category_id: number;
  operation_status: number;
  title: string;
  description: string;
  raised_by: number;
  assigned_to: number | null;
  product_id: number | null;
  priority: number;
  company_id: any;
  company_type: string;
  company_name?: string;
  due_date?: string;
  createdAt: string;
  updatedAt: string;
  status: number;
}

interface CompanyOption {
  value: number;
  label: string;
  companyName: string;
}

interface Category {
  key: number;
  Category: string;
  Company?: string;
  CompanyId?: number;
  CompanyType?: string;
  Status?: string;
}

type FetchUI = () => Promise<void>;

interface TicketListModalProps {
  categories?: Category[];
  selectedTicket?: Ticket;
  fetchUI?: FetchUI;
  companyChoose?: CompanyOption[];
  employeeDetail?: any[];
  user?: any;
  signature?: string;
}

// Static options - defined outside component
const PRIORITY_OPTIONS: Option[] = [
  { value: "", label: "Select Priority" },
  { value: "2", label: "High" },
  { value: "0", label: "Low" },
  { value: "1", label: "Medium" },
];

const STATUS_OPTIONS: Option[] = [
  { value: "", label: "Select Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const OPERATION_STATUS_OPTIONS: Option[] = [
  { value: "", label: "Select Operation Status" },
  { value: "1", label: "New Ticket" },
  { value: "2", label: "Open Ticket" },
  { value: "3", label: "Pending Ticket" },
  { value: "4", label: "Closed Ticket" },
];

const TicketListModal = ({
  selectedTicket,
  fetchUI,
  companyChoose = [],
  employeeDetail = [],
  categories = [],
  user: propUser,
  signature: propSignature,
}: TicketListModalProps) => {
  const reduxUser = useSelector((state: any) => state.user.user);
  const user = propUser || reduxUser;

  const localStorageSignature = localStorage.getItem("signature") || "";
  const signature = propSignature || localStorageSignature;

  const [addFormData, setAddFormData] = useState({
    title: "",
    category_id: "",
    description: "",
    raised_by: "",
    assigned_to: "",
    product_id: "",
    priority: "",
    operation_status: "",
    status: "",
    due_date: "",
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    title: "",
    category_id: "",
    description: "",
    raised_by: "",
    assigned_to: "",
    product_id: "",
    priority: "",
    operation_status: "",
    status: "",
    due_date: "",
  });

  const [filteredEmployees, setFilteredEmployees] = useState<Option[]>([]);
  const [showAssignTo, setShowAssignTo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formDataCompanyChoose, setFormDataCompanyChoose] = useState({
    signature: signature,
    companyName: "Select Company",
    companyId: "",
    companyType: "Ticket",
  });

  // Cache for employee images
  const employeeImageCache = useMemo(() => {
    if (!employeeDetail.length) return {};

    const cache: Record<string, string> = {};
    employeeDetail.forEach((emp: any) => {
      cache[emp.key] = getEmployeeImageUrl(emp.key, employeeDetail, user);
    });
    return cache;
  }, [employeeDetail, user]);

  // Memoized category options
  const categoryOptions = useMemo(() => {
    return [
      { value: "", label: "Select Category" },
      ...(categories?.map((cat: any) => ({
        value: String(cat.key),
        label: cat.Category,
      })) || []),
    ];
  }, [categories]);

  // Memoized company options
  const companyOptions = useMemo(() => {
    return companyChoose.map((company) => ({
      value: String(company.value),
      label: company.label,
    }));
  }, [companyChoose]);

  // Memoized filtered employees
  useEffect(() => {
    if (!employeeDetail.length || !formDataCompanyChoose.companyId) {
      setFilteredEmployees([]);
      return;
    }

    const filtered = employeeDetail
      .filter((emp: any) => {
        return (
          emp.CompanyId == formDataCompanyChoose.companyId &&
          emp.CompanyType == formDataCompanyChoose.companyType &&
          emp.status == 1
        );
      })
      .map((emp: any) => {
        const fullName = emp.Lname
          ? `${emp.Fname} ${emp.Lname}`
          : emp.Name || emp.Fname;
        return {
          value: String(emp.key),
          label: `${fullName} - ${emp.Designation}`,
        };
      });

    setFilteredEmployees(filtered);
  }, [employeeDetail, formDataCompanyChoose.companyId, formDataCompanyChoose.companyType]);

  // Handle company change
  const handleCompanyChange = useCallback((selectedOption: any) => {
    if (!selectedOption) return;

    const [companyName, companyType] = selectedOption.label.split("-");
    setFormDataCompanyChoose((prev) => ({
      ...prev,
      companyId: selectedOption.value,
      companyName: companyName?.trim() || "Select Company",
      companyType: companyType?.trim() || "Ticket",
    }));
  }, []);

  // Load edit data when selectedTicket changes
  useEffect(() => {
    if (!selectedTicket?.company_id || !selectedTicket?.company_type) {
      return;
    }

    const loadEditData = async () => {
      setIsLoading(true);
      try {
        // Batch state updates
        const formattedDueDate = selectedTicket.due_date
          ? new Date(selectedTicket.due_date).toISOString().split("T")[0]
          : "";

        setFormDataCompanyChoose({
          signature: signature,
          companyId: selectedTicket.company_id,
          companyName: selectedTicket.company_name || "Selected Company",
          companyType: selectedTicket.company_type,
        });

        setEditFormData({
          id: String(selectedTicket.id),
          title: selectedTicket.title,
          category_id: String(selectedTicket.category_id),
          description: selectedTicket.description,
          raised_by: String(selectedTicket.raised_by),
          assigned_to: selectedTicket.assigned_to ? String(selectedTicket.assigned_to) : "",
          product_id: selectedTicket.product_id ? String(selectedTicket.product_id) : "",
          priority: String(selectedTicket.priority),
          operation_status: String(selectedTicket.operation_status),
          status: selectedTicket.status ? String(selectedTicket.status) : "",
          due_date: formattedDueDate,
        });

        setShowAssignTo(selectedTicket.operation_status !== 1);
      } catch (error) {
        console.error("Failed to load edit data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEditData();
  }, [selectedTicket, signature]);

  // Memoized employee options with images
  const employeeOptionsWithImages = useMemo(() => {
    return filteredEmployees.map((opt) => ({
      label: opt.label,
      value: opt.value,
      imageUrl: employeeImageCache[opt.value] || "",
    }));
  }, [filteredEmployees, employeeImageCache]);

  const employeeOptionsWithUnassigned = useMemo(() => {
    return [
      { value: "", label: "Unassigned", imageUrl: "" },
      ...employeeOptionsWithImages,
    ];
  }, [employeeOptionsWithImages]);

  // Form handlers
  const handleAddChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setAddFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleAddSelectChange = useCallback(
    (name: string) => (selectedOption: Option | null) => {
      const newValue = selectedOption?.value || "";
      setAddFormData((prev) => ({ ...prev, [name]: newValue }));
      if (name === "operation_status") {
        setShowAssignTo(newValue !== "1");
        if (newValue === "1") {
          setAddFormData((prev) => ({ ...prev, due_date: "" }));
        }
      }
    },
    []
  );

  const handleEditChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleEditSelectChange = useCallback(
    (name: string) => (selectedOption: Option | null) => {
      const newValue = selectedOption?.value || "";
      setEditFormData((prev) => ({ ...prev, [name]: newValue }));
      if (name === "operation_status") {
        setShowAssignTo(newValue !== "1");
        if (newValue === "1") {
          setEditFormData((prev) => ({ ...prev, due_date: "" }));
        }
      }
    },
    []
  );

  const resetAddForm = useCallback(() => {
    setAddFormData({
      title: "",
      category_id: "",
      description: "",
      raised_by: "",
      assigned_to: "",
      product_id: "",
      priority: "",
      operation_status: "",
      status: "",
      due_date: "",
    });
    setShowAssignTo(false);
  }, []);

  const handleAddSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (formDataCompanyChoose.companyId === "") {
      showBasicAlert("Please select a company.");
      return;
    }

    if (showAssignTo && !addFormData.assigned_to) {
      showBasicAlert("Please assign the ticket to an employee.");
      return;
    }

    if (showAssignTo && !addFormData.due_date) {
      showBasicAlert("Please select a due date.");
      return;
    }

    if (showAssignTo && addFormData.due_date) {
      const selectedDate = new Date(addFormData.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        showBasicAlert("Due date cannot be in the past.");
        return;
      }
    }

    const requiredFields = [
      { field: "title", label: "Title" },
      { field: "category_id", label: "Category" },
      { field: "description", label: "Description" },
      { field: "priority", label: "Priority" },
      { field: "operation_status", label: "Operation Status" },
    ];

    let raisedByValue = addFormData.raised_by;
    if (
      user?.userDetails?.role_char?.toLowerCase() === "employee" ||
      user?.userDetails?.role_char?.toLowerCase() === "employeeadmin"
    ) {
      raisedByValue = user.userDetails.id;
    }

    for (let item of requiredFields) {
      const value = (addFormData as any)[item.field];
      if (!value) {
        showBasicAlert(`Please enter/select ${item.label}.`);
        return;
      }
    }

    try {
      const payload: any = {
        title: addFormData.title,
        category_id: parseInt(addFormData.category_id),
        description: addFormData.description,
        raised_by: parseInt(raisedByValue),
        assigned_to: addFormData.assigned_to ? parseInt(addFormData.assigned_to) : null,
        priority: parseInt(addFormData.priority),
        operation_status: parseInt(addFormData.operation_status),
        // status: parseInt(addFormData.status),
        status: 1,
        company_id: formDataCompanyChoose.companyId,
        company_type: formDataCompanyChoose.companyType,
        signature,
      };

      if (showAssignTo && addFormData.due_date) {
        payload.due_date = addFormData.due_date;
      }

      if (addFormData.product_id) {
        payload.product_id = parseInt(addFormData.product_id);
      }

      const response = await api.post("raiseTicketsService/addRaiseTicket", payload);
      const result = response.data?.message[0];
      showBasicAlert(result.msg);
      document.getElementById("close-btn-add-ticket-model")?.click();

      if (result.status) {
        resetAddForm();
      }
      await fetchUI?.();
    } catch (error: any) {
      console.error("Error adding ticket:", error);
      showBasicAlert(error?.data?.message?.[0]?.msg || "Something went wrong.");
    }
  }, [addFormData, formDataCompanyChoose, showAssignTo, user, signature, fetchUI, resetAddForm]);

  const handleEditSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (showAssignTo && !editFormData.assigned_to) {
      showBasicAlert("Please assign the ticket to an employee.");
      return;
    }

    if (showAssignTo && editFormData.due_date) {
      const selectedDate = new Date(editFormData.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        showBasicAlert("Due date cannot be in the past.");
        return;
      }
    }

    try {
      const payload: any = {
        id: editFormData.id ? parseInt(editFormData.id) : undefined,
        title: editFormData.title,
        category_id: editFormData.category_id ? parseInt(editFormData.category_id) : null,
        description: editFormData.description,
        raised_by: editFormData.raised_by ? Number(editFormData.raised_by) : undefined,
        assigned_to: editFormData.assigned_to ? Number(editFormData.assigned_to) : null,
        priority: editFormData.priority ? parseInt(editFormData.priority) : null,
        operation_status: editFormData.operation_status ? parseInt(editFormData.operation_status) : null,
        status: editFormData.status ? parseInt(editFormData.status) : null,
        company_id: selectedTicket?.company_id,
        company_type: selectedTicket?.company_type,
        signature,
      };

      if (showAssignTo && editFormData.due_date) {
        payload.due_date = editFormData.due_date;
      }

      if (editFormData.product_id) {
        payload.product_id = parseInt(editFormData.product_id);
      }

      const response = await api.post("raiseTicketsService/updateRaiseTicket", payload);
      const result = response.data?.message[0];
      document.getElementById("close-btn-edit-ticket-model")?.click();
      showBasicAlert(result.msg);
      await fetchUI?.();
    } catch (error: any) {
      console.error("Error updating ticket:", error);
      showBasicAlert("Failed to update ticket.");
    }
  }, [editFormData, showAssignTo, selectedTicket, signature, fetchUI]);

  // Modal reset on open
  useEffect(() => {
    const addModal = document.getElementById("add_ticket");
    const handleModalShow = () => resetAddForm();

    addModal?.addEventListener("show.bs.modal", handleModalShow);
    return () => addModal?.removeEventListener("show.bs.modal", handleModalShow);
  }, [resetAddForm]);

  // Memoized form renderers
  const renderAddStep1 = useCallback(() => (
    <div className="modal-body">
      <div className="row">
        <div className="col-md-12">
          <div>
            <label className="form-label">
              Company <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={companyOptions}
              defaultValue={{
                value: formDataCompanyChoose.companyId,
                label: `${formDataCompanyChoose.companyName}-${formDataCompanyChoose.companyType}`,
              }}
              onChange={handleCompanyChange}
            />
          </div>
        </div>
      </div>
    </div>
  ), [companyOptions, formDataCompanyChoose, handleCompanyChange]);

  const renderAddStep2 = useCallback(() => (
    <div className="modal-body">
      {isLoading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading data...</p>
        </div>
      ) : (
        <div className="row">
          {/* Category Field */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Category <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={categoryOptions}
              defaultValue={categoryOptions.find(opt => opt.value === addFormData.category_id) || categoryOptions[0]}
              onChange={handleAddSelectChange("category_id")}
            />
          </div>

          {/* Title Field */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="title"
              value={addFormData.title}
              onChange={handleAddChange}
              placeholder="Enter Title"
              required
            />
          </div>

          {/* Raised By */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Raised By
            </label>
            <ImageSelectDropMenu
              className="select"
              options={employeeOptionsWithImages}
              defaultValue={employeeOptionsWithImages.find(opt => {
                const selectedId =
                  user?.userDetails?.role_char?.toLowerCase() === "employee" ||
                    user?.userDetails?.role_char?.toLowerCase() === "employeeadmin"
                    ? user?.userDetails?.id
                    : addFormData.raised_by;
                return parseInt(opt.value) === parseInt(selectedId);
              })}
              onChange={handleAddSelectChange("raised_by")}
              isDisabled={
                user?.userDetails?.role_char?.toLowerCase() === "employee" ||
                user?.userDetails?.role_char?.toLowerCase() === "employeeadmin"
              }
              placeHolder="Select Member"
            />
          </div>

          {/* Priority */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Priority <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={PRIORITY_OPTIONS}
              defaultValue={PRIORITY_OPTIONS.find(opt => opt.value === addFormData.priority) || PRIORITY_OPTIONS[0]}
              onChange={handleAddSelectChange("priority")}
            />
          </div>

          {/* Status */}
          {/* <div className="mb-3 col-md-6">
            <label className="form-label">
              Status <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={STATUS_OPTIONS}
              defaultValue={STATUS_OPTIONS.find(opt => opt.value === addFormData.status) || STATUS_OPTIONS[0]}
              onChange={handleAddSelectChange("status")}
            />
          </div> */}

          {/* Operation Status */}
          <div className="mb-0 col-md-6">
            <label className="form-label">
              Operation Status <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={OPERATION_STATUS_OPTIONS}
              defaultValue={OPERATION_STATUS_OPTIONS.find(opt => opt.value === addFormData.operation_status) || OPERATION_STATUS_OPTIONS[0]}
              onChange={handleAddSelectChange("operation_status")}
            />
          </div>

          {/* Assign To - Conditional */}
          {showAssignTo && (
            <div className="mb-0 col-md-6">
              <label className="form-label">
                Assign To <span className="text-danger">*</span>
              </label>
              <ImageSelectDropMenu
                className="select"
                options={employeeOptionsWithImages}
                defaultValue={employeeOptionsWithImages.find(opt => opt.value === addFormData.assigned_to)}
                onChange={handleAddSelectChange("assigned_to")}
                placeHolder="Select Member"
              />
            </div>
          )}

          {/* Due Date - Conditional */}
          {showAssignTo && (
            <div className="mb-3 col-md-6">
              <label className="form-label">
                Due Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                name="due_date"
                value={addFormData.due_date}
                onChange={handleAddChange}
                min={new Date().toISOString().split("T")[0]}
                required
              />
              <small className="text-muted">Select the deadline for this ticket</small>
            </div>
          )}

          {/* Description */}
          <div className="mb-3 col-md-12">
            <label className="form-label">
              Ticket Description <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              name="description"
              value={addFormData.description}
              onChange={handleAddChange}
              placeholder="Enter Description"
              rows={3}
              required
            />
          </div>
        </div>
      )}
    </div>
  ), [isLoading, categoryOptions, addFormData, handleAddChange, handleAddSelectChange,
    showAssignTo, employeeOptionsWithImages, user]);

  const renderEditForm = useCallback(() => (
    <div className="modal-body">
      {isLoading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading data...</p>
        </div>
      ) : (
        <div className="row">
          {/* Category */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Category <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={categoryOptions}
              defaultValue={categoryOptions.find(opt => opt.value === editFormData.category_id) || categoryOptions[0]}
              onChange={handleEditSelectChange("category_id")}
            />
          </div>

          {/* Title */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="title"
              value={editFormData.title}
              onChange={handleEditChange}
              placeholder="Enter Title"
              required
            />
          </div>

          {/* Priority */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Priority <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={PRIORITY_OPTIONS}
              defaultValue={PRIORITY_OPTIONS.find(opt => opt.value === editFormData.priority) || PRIORITY_OPTIONS[0]}
              onChange={handleEditSelectChange("priority")}
            />
          </div>

          {/* Operation Status */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Operation Status <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={OPERATION_STATUS_OPTIONS}
              defaultValue={OPERATION_STATUS_OPTIONS.find(opt => opt.value === editFormData.operation_status) || OPERATION_STATUS_OPTIONS[0]}
              onChange={handleEditSelectChange("operation_status")}
            />
          </div>

          {/* Status */}
          <div className="mb-3 col-md-6" >
            <label className="form-label">
              Status <span className="text-danger">*</span>
            </label>
            <CommonSelect
              className="select"
              options={STATUS_OPTIONS}
              defaultValue={STATUS_OPTIONS.find(opt => opt.value === editFormData.status) || STATUS_OPTIONS[0]}
              onChange={handleEditSelectChange("status")}
              isDisabled
            />
          </div>

          {/* Raised By */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Raised By <span className="text-danger">*</span>
            </label>
            <ImageSelectDropMenu
              className="select"
              options={employeeOptionsWithImages}
              defaultValue={employeeOptionsWithImages.find(emp => emp.value === editFormData.raised_by)}
              onChange={handleEditSelectChange("raised_by")}
              placeHolder="Select Member"
            />
          </div>

          {/* Assign To */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Assign To {showAssignTo && <span className="text-danger">*</span>}
            </label>
            <ImageSelectDropMenu
              className="select"
              options={employeeOptionsWithUnassigned}
              defaultValue={
                !editFormData.assigned_to
                  ? { value: "", label: "Unassigned", imageUrl: "" }
                  : employeeOptionsWithImages.find(emp => emp.value === editFormData.assigned_to)
              }
              onChange={handleEditSelectChange("assigned_to")}
              placeHolder="Select Member"
            />
          </div>

          {/* Due Date */}
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Due Date {showAssignTo && <span className="text-danger">*</span>}
            </label>
            <input
              type="date"
              className="form-control"
              name="due_date"
              value={editFormData.due_date}
              onChange={handleEditChange}
              min={new Date().toISOString().split("T")[0]}
              required={showAssignTo}
            />
            <small className="text-muted">Select the deadline for this ticket</small>
          </div>

          {/* Description */}
          <div className="mb-3 col-md-12">
            <label className="form-label">
              Ticket Description <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              name="description"
              value={editFormData.description}
              onChange={handleEditChange}
              placeholder="Enter Description"
              rows={3}
              required
            />
          </div>
        </div>
      )}
    </div>
  ), [isLoading, categoryOptions, editFormData, handleEditChange, handleEditSelectChange,
    showAssignTo, employeeOptionsWithImages, employeeOptionsWithUnassigned]);

  return (
    <>
      {/* Add Ticket Modal */}
      <div className="modal fade" id="add_ticket">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Ticket</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleAddSubmit}>
              {renderAddStep1()}
              {formDataCompanyChoose.companyId !== "" && renderAddStep2()}
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  id="close-btn-add-ticket-model"
                >
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? "Adding..." : "Add Ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Ticket Modal */}
      <div className="modal fade" id="edit_ticket">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Ticket</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              {renderEditForm()}
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  id="close-btn-edit-ticket-model"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Updating..." : "Update Ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketListModal;